import React from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App.js";
import { startHealthWatchdog } from "./diag/health.js";
import "./styles.css";

// A new release is applied by the service worker. A page that just started (the operator has
// not begun anything) reloads itself so the new version runs at once, instead of on the second
// start (owner 2026-10-29: "do I have to restart twice every time?"). A page that has been up
// longer keeps running; the waiting version takes over at the next start.
startHealthWatchdog();

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Early in a start: take it now. Later: the app applies it the next time the operator stands in the
    // menu (owner 2026-11-02: the phone kept running the old version through several reloads).
    if (performance.now() < 20_000) { void updateSW(true); return; }
    (window as unknown as { __trailApplyUpdate?: () => void }).__trailApplyUpdate = () => void updateSW(true);
    window.dispatchEvent(new Event("trail:update-waiting"));
  },
});

/** A crash must never leave a blank page on a phone: say so, offer a reload that drops the
 *  device's caches (bundle, draft, prefs stay) so a stale cache cannot keep it broken. */
class Boundary extends React.Component<{ children: React.ReactNode }, { error: string | null }> {
  override state = { error: null as string | null };
  static getDerivedStateFromError(e: unknown) {
    return { error: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="screen" style={{ padding: 24, gap: 16, display: "flex", flexDirection: "column" }}>
        <h1 style={{ margin: 0 }}>Något gick fel</h1>
        <p className="hint">{this.state.error}</p>
        <button
          type="button"
          className="btn primary"
          onClick={async () => {
            try {
              localStorage.removeItem("trail.referenceBundle");
              if ("caches" in window) for (const k of await caches.keys()) await caches.delete(k);
              const regs = await navigator.serviceWorker?.getRegistrations();
              for (const r of regs ?? []) await r.unregister();
            } finally {
              location.reload();
            }
          }}
        >
          Ladda om
        </button>
      </div>
    );
  }
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Boundary>
      <App />
    </Boundary>
  </React.StrictMode>,
);
